import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';
import * as XLSX from 'xlsx';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'active' ? 'badge badge-success' : s === 'inactive' ? 'badge badge-danger' : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function VendorTable({ onEdit }) {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ name: '', state: '', status: '', invoiced: false });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const menuRef = useRef();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchVendors = async () => {
    try {
      const data = await fetchWithCorpId(`/vendors?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setVendors(data); setFilteredVendors(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setVendors(Array.isArray(content) ? content : []); setFilteredVendors(Array.isArray(content) ? content : []);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to load vendors'); setVendors([]); setFilteredVendors([]);
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await fetchWithCorpId(`/vendors/${id}`, { method: 'DELETE' }); fetchVendors(); }
    catch { alert('Failed to delete vendor'); }
  };

  useEffect(() => {
    fetchVendors();
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) {} };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, vendors]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const applyFilters = () => {
    if (!Array.isArray(vendors)) { setFilteredVendors([]); return; }
    setFilteredVendors(vendors.filter(v =>
      (!filter.name || v.name?.toLowerCase().includes(filter.name.toLowerCase())) &&
      (!filter.state || v.state?.toLowerCase().includes(filter.state.toLowerCase())) &&
      (filter.invoiced === false || Boolean(v.invoiced) === true) &&
      (!filter.status || v.status?.toLowerCase() === filter.status.toLowerCase())
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key || !Array.isArray(filteredVendors)) return;
    setFilteredVendors(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFilter(p => ({ ...p, [name]: type === 'checkbox' ? Boolean(checked) : value }));
  };
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredVendors.map(v => ({
      Name: v.name, Address: v.address, City: v.city, State: v.state,
      Zip: v.zip, Status: v.status, 'Start Date': v.startDate,
      Invoiced: v.invoiced, 'Access Code': v.accessCode,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
    XLSX.writeFile(wb, 'vendors.xlsx');
  };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Vendors</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Manage vendor accounts and information</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-info" onClick={handleDownloadExcel}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn-primary" onClick={() => navigate('/on/vendors/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Vendor
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input name="name" placeholder="Name" value={filter.name} onChange={handleFilterChange} />
        <input name="state" placeholder="State" value={filter.state} onChange={handleFilterChange} />
        <select name="status" value={filter.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" name="invoiced" checked={!!filter.invoiced} onChange={handleFilterChange} style={{ width: 'auto', flex: 'none' }} />
          Invoiced only
        </label>
        <button className="btn-secondary" onClick={() => setFilter({ name: '', state: '', status: '', invoiced: false })}>Clear</button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading vendors…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name <SortIcon col="name" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('address')}>Address <SortIcon col="address" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('city')}>City <SortIcon col="city" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('state')}>State <SortIcon col="state" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('zip')}>Zip <SortIcon col="zip" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startDate')}>Start Date <SortIcon col="startDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('invoiced')}>Invoiced <SortIcon col="invoiced" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('accessCode')}>Access Code <SortIcon col="accessCode" sortConfig={sortConfig} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.length === 0 ? (
                <tr className="state-row"><td colSpan="9">No vendors found.</td></tr>
              ) : filteredVendors.map(v => (
                <tr key={v.id}>
                  <td><span className="table-link" onClick={() => onEdit(v)}>{v.name}</span></td>
                  <td>{v.address}</td>
                  <td>{v.city}</td>
                  <td>{v.state}</td>
                  <td>{v.zip}</td>
                  <td><StatusBadge status={v.status} /></td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{v.startDate}</td>
                  <td>
                    {Boolean(v.invoiced)
                      ? <span className="badge badge-success">Yes</span>
                      : <span className="badge badge-neutral">No</span>}
                  </td>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 7px', borderRadius: '5px', fontSize: '12.5px', color: '#1e293b' }}>{v.accessCode}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorTable;
